import {
  type ElementType,
  type ReactNode,
  type Ref,
  useEffect,
  useRef,
  useState,
} from 'react'
import {
  m,
  useMotionValueEvent,
  useTransform,
  type MotionValue,
} from 'motion/react'
import cx from 'classnames'

import { IconButton } from '@/components/IconButton'
import { clamp, lerp } from '@/utils/math-util'
import {
  AnimationProgressProvider,
  useAnimationProgress,
} from '@/contexts/animation-progress-context'
import { AvatarProgress } from '@/components/AvatarProgress'
import { type AvatarProps, Avatar } from '@/components/Avatar'
import styles from './SideSheetHeader.module.css'
import { useFallbackMotionValue } from '@/hooks/motion-hooks'
import { ScrollShadow } from '@/components/ScrollShadow'

/**
 * How much to move the header up.
 */
const DELTA = 150

interface SideSheetHeaderProps {
  children?: ReactNode
  scrollOffset?: MotionValue<number>
  onClose?: () => unknown
  bordered?: boolean
  /**
   * Triggers whenever the stickiness changes.
   */
  onStickyChange?: (isSticky: boolean) => unknown
}

/**
 * Header for a side sheet, which compacts as the content scrolls.
 */
export function SideSheetHeader({
  onClose,
  children,
  onStickyChange,
  bordered,
  scrollOffset,
}: SideSheetHeaderProps) {
  const [isSticky, setIsSticky] = useState(false)
  const offset = useFallbackMotionValue(scrollOffset)

  const rootRef = useRef<HTMLDivElement | null>(null)
  const compactionAnimationProgress = useTransform(offset, (v) =>
    clamp(v / DELTA),
  )
  useMotionValueEvent(compactionAnimationProgress, 'change', (v) =>
    setIsSticky(v === 1),
  )

  useEffect(() => {
    onStickyChange?.(isSticky)
  }, [onStickyChange, isSticky])
  const rootStyle = {
    transform: useTransform(
      offset,
      (v) => `translateY(${-Math.min(v, DELTA)}px)`,
    ),
  }
  const closeButtonStyle = {
    transform: useTransform(
      compactionAnimationProgress,
      (v) => `translateY(${lerp(0, DELTA, v)}px)`,
    ),
  }
  // Don't start displaying the shadow until the header sticks.
  const shadowOpacity = useTransform(offset, (v) => clamp(v / DELTA - 1))

  return (
    <AnimationProgressProvider progress={compactionAnimationProgress}>
      <m.div
        className={cx(styles.root, bordered && styles.bordered)}
        ref={rootRef}
        style={rootStyle}
      >
        {children}
        {onClose && (
          <m.div className={styles.top} style={closeButtonStyle}>
            <IconButton name="back" label="Close" onPress={() => onClose()} />
          </m.div>
        )}
        <ScrollShadow borderless opacity={shadowOpacity} />
      </m.div>
    </AnimationProgressProvider>
  )
}

interface SideSheetHeaderContentProps {
  children?: ReactNode
}

/**
 * The content area of a side sheet header.
 */
export function SideSheetHeaderContent({
  children,
}: SideSheetHeaderContentProps) {
  return <div className={styles.headerContent}>{children}</div>
}

interface SideSheetHeaderTextProps {
  children?: ReactNode
}

/**
 * Text in a side sheet header, which moves up as the header compacts.
 */
export function SideSheetHeaderText({ children }: SideSheetHeaderTextProps) {
  const compactionAnimationProgress = useAnimationProgress()
  const headerTextStyle = {
    // This will move the text up without changing its' computed height.
    transform: useTransform(
      compactionAnimationProgress,
      (v) => `translateY(${lerp(0, 12, v)}px)`,
    ),
  }

  return (
    <m.div className={styles.headerText} style={headerTextStyle}>
      {children}
    </m.div>
  )
}

interface SideSheetHeaderAvatarProps {
  /**
   * When `null`, the avatar will be rendered a bit smaller
   * so the progress around it will take up the required space,
   * even if the progress radial isn't being rendered at that time.
   *
   * When `undefined`, renders the avatar at full size.
   */
  progress?: number | null
  /**
   * Avatar subject
   */
  subject: AvatarProps['subject']
  /**
   * If specified, renders this avatar in the bottom right corner.
   * Don't use this when using progress.
   */
  cornerSubject?: AvatarProps['subject']
}

/**
 * An avatar in a side sheet header, which fades out as the header compacts.
 */
export function SideSheetHeaderAvatar({
  progress,
  subject,
  cornerSubject,
}: SideSheetHeaderAvatarProps) {
  const compactionAnimationProgress = useAnimationProgress()
  const avatarContainerStyle = {
    opacity: useTransform(compactionAnimationProgress, (v) => lerp(1, 0, v)),
  }
  const avatar = (
    <Avatar size={progress === undefined ? 100 : 80} subject={subject} />
  )
  return (
    <m.div
      className={cx(
        styles.avatarContainer,
        progress !== undefined && styles.hasProgress,
      )}
      style={avatarContainerStyle}
    >
      <div className={styles.avatarContainerItem}>
        {progress === undefined ? (
          avatar
        ) : (
          <AvatarProgress size={100} thickness={3} progress={progress}>
            {avatar}
          </AvatarProgress>
        )}
        {cornerSubject && (
          <div className={styles.cornerAvatar}>
            <Avatar size={40} subject={cornerSubject} />
          </div>
        )}
      </div>
    </m.div>
  )
}

interface SideSheetHeaderTitleProps {
  children?: ReactNode
  isButton?: boolean
  icon?: ReactNode
  title?: string
  onPress?: () => unknown
  ref?: Ref<HTMLElement>
}

/**
 * The title of a side sheet header, optionally pressable.
 */
export function SideSheetHeaderTitle({
  isButton,
  onPress,
  children,
  icon,
  title,
  ref,
}: SideSheetHeaderTitleProps) {
  const Elem = (isButton ? 'a' : 'h3') as ElementType
  return (
    <Elem
      title={title}
      ref={ref}
      onClick={onPress}
      className={isButton ? styles.titleButton : styles.title}
    >
      <span className={styles.titleText}>{children}</span>
      {icon && <span className={styles.titleButtonIcon}>{icon}</span>}
    </Elem>
  )
}

interface SideSheetHeaderDescriptionProps {
  children?: ReactNode
}

/**
 * The description of a side sheet header.
 */
export function SideSheetHeaderDescription({
  children,
}: SideSheetHeaderDescriptionProps) {
  return <p className={styles.description}>{children}</p>
}
